import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Panel } from "@/components/Panel";
import { Paginacion } from "@/components/Paginacion";
import { VehiculoForm, validarVehiculo } from "@/components/VehiculoForm";
import { HondaActualizacionDialog } from "@/components/HondaActualizacionDialog";
import { useBusquedaLive } from "@/lib/busquedaHeader";
import { useAuth } from "@/lib/auth";
import { Pencil, X, Plus, RefreshCw, History } from "lucide-react";

export default function VehiculosGestion() {
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const [vehiculos, setVehiculos] = useState([]);
  const [q] = useBusquedaLive("Buscar en vehículos…");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [cargando, setCargando] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editPlacaOriginal, setEditPlacaOriginal] = useState("");
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [hondaLoteId, setHondaLoteId] = useState(null);

  useEffect(() => {
    (async () => {
      const [c, m] = await Promise.all([api.get("/clientes"), api.get("/modelos")]);
      setClientes(c.data); setModelos(m.data);
    })();
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get("/vehiculos", { params: { q: q.trim() || undefined, page, page_size: pageSize } });
      setVehiculos(r.data.items);
      setMeta({ total: r.data.total, total_pages: r.data.total_pages });
    } finally { setCargando(false); }
  }, [q, page, pageSize]);

  useEffect(() => { setPage(1); }, [q, pageSize]);
  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const eliminar = async (v) => {
    if (!window.confirm(`¿Eliminar el vehículo con placa "${v.placa}"?`)) return;
    try { await api.delete(`/vehiculos/${v.id}`); toast.success("Vehículo eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const guardarEdicion = async () => {
    const e = validarVehiculo(edit, editPlacaOriginal);
    setErrores(e);
    if (Object.keys(e).length) return;
    setGuardando(true);
    try {
      const { modelo, cliente, ...body } = edit;
      await api.patch(`/vehiculos/${edit.id}`, { ...body, anio: parseInt(edit.anio, 10) });
      toast.success("Vehículo actualizado");
      setEdit(null); setErrores({});
      cargar();
    } catch (err) {
      const msg = errorMsg(err);
      if (err?.response?.status === 409) {
        setErrores(msg.toLowerCase().includes("chasis") ? { chasis: msg } : { placa: msg });
      }
      toast.error(msg);
    } finally { setGuardando(false); }
  };

  const toggleSeleccionado = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const idsPagina = vehiculos.map((v) => v.id);
  const todosSeleccionadosPagina = idsPagina.length > 0 && idsPagina.every((id) => seleccionados.has(id));
  const toggleSeleccionarTodosPagina = () => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (todosSeleccionadosPagina) idsPagina.forEach((id) => next.delete(id));
      else idsPagina.forEach((id) => next.add(id));
      return next;
    });
  };

  const actualizarDesdeHonda = async () => {
    try {
      const { data } = await api.post("/vehiculos/honda-actualizar", { vehiculo_ids: Array.from(seleccionados) });
      if (!data.total) {
        toast.info("Los vehículos seleccionados ya tenían una actualización en curso");
        return;
      }
      setSeleccionados(new Set());
      setHondaLoteId(data.lote_id);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const verUltimaActualizacion = async () => {
    try {
      const { data } = await api.get("/honda-actualizaciones/ultimo-lote");
      if (!data.lote_id) { toast.info("No hay actualizaciones previas"); return; }
      setHondaLoteId(data.lote_id);
    } catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <div data-testid="vehiculos-gestion-page" className="space-y-4">
      <Panel titulo="Gestión de Vehículos" testId="vehiculos-gestion-panel">
        <div className="flex justify-end items-center gap-2 mb-4 flex-wrap">
          {tienePermiso("honda.actualizar_vehiculos") && (
            <>
              <Button variant="outline" data-testid="vehiculos-ver-ultima-actualizacion-btn"
                onClick={verUltimaActualizacion} className="border-slate-300">
                <History size={16} className="mr-1" /> Ver última actualización
              </Button>
              <Button variant="outline" data-testid="vehiculos-honda-actualizar-btn"
                onClick={actualizarDesdeHonda} disabled={seleccionados.size === 0}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                <RefreshCw size={16} className="mr-1" /> Actualizar desde Honda ({seleccionados.size})
              </Button>
            </>
          )}
          {tienePermiso("vehiculos.crear") && (
            <Button data-testid="vehiculo-nuevo-btn" onClick={() => navigate("/vehiculos")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus size={16} className="mr-1" /> Nuevo vehículo
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table data-testid="vehiculos-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                {tienePermiso("honda.actualizar_vehiculos") && (
                  <th className="py-2.5 pr-3 font-semibold w-8">
                    <Checkbox data-testid="vehiculos-seleccionar-todos-pagina-checkbox"
                      checked={todosSeleccionadosPagina} onCheckedChange={toggleSeleccionarTodosPagina} />
                  </th>
                )}
                <th className="py-2.5 pr-3 font-semibold">Placa</th>
                <th className="py-2.5 pr-3 font-semibold">Marca</th>
                <th className="py-2.5 pr-3 font-semibold">Modelo</th>
                <th className="py-2.5 pr-3 font-semibold">Año</th>
                <th className="py-2.5 pr-3 font-semibold">Cliente</th>
                <th className="py-2.5 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {vehiculos.map((v) => (
                <tr key={v.id} data-testid={`vehiculo-row-${v.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  {tienePermiso("honda.actualizar_vehiculos") && (
                    <td className="py-2.5 pr-3">
                      <Checkbox data-testid={`vehiculo-seleccionar-checkbox-${v.id}`}
                        checked={seleccionados.has(v.id)} onCheckedChange={() => toggleSeleccionado(v.id)} />
                    </td>
                  )}
                  <td className="py-2.5 pr-3 font-medium text-slate-800">{v.placa}</td>
                  <td className="py-2.5 pr-3">{v.marca}</td>
                  <td className="py-2.5 pr-3">{v.modelo?.nombre_mostrar || "—"}</td>
                  <td className="py-2.5 pr-3">{v.anio}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{v.cliente?.nombre || "—"}</td>
                  <td className="py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {tienePermiso("vehiculos.editar") && (
                      <button data-testid={`vehiculo-editar-btn-${v.id}`} title="Editar"
                        onClick={() => { setEdit({ ...v, anio: String(v.anio), chasis: v.chasis || "" }); setEditPlacaOriginal(v.placa || ""); setErrores({}); }}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                        <Pencil size={13} />
                      </button>
                      )}
                      {tienePermiso("vehiculos.borrar") && (
                      <button data-testid={`vehiculo-eliminar-btn-${v.id}`} title="Eliminar" onClick={() => eliminar(v)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <X size={14} />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {vehiculos.length === 0 && (
                <tr><td colSpan={tienePermiso("honda.actualizar_vehiculos") ? 7 : 6} className="py-8 text-center text-slate-400" data-testid="vehiculos-vacio">
                  No se encontraron vehículos
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginacion page={page} totalPages={meta.total_pages} total={meta.total} pageSize={pageSize}
          onPage={setPage} onPageSize={setPageSize} cargando={cargando} />
      </Panel>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) { setEdit(null); setErrores({}); } }}>
        <DialogContent data-testid="vehiculo-editar-dialog" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Editar vehículo</DialogTitle>
            <DialogDescription>Modifique los datos del vehículo y guarde los cambios.</DialogDescription>
          </DialogHeader>
          {edit && (
            <>
              <VehiculoForm valor={edit} onChange={setEdit} errores={errores} clientes={clientes} modelos={modelos} />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" data-testid="vehiculo-editar-cancelar-btn"
                  className="border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
                  onClick={() => { setEdit(null); setErrores({}); }}>Cancelar</Button>
                <Button data-testid="vehiculo-guardar-btn" onClick={guardarEdicion} disabled={guardando}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
                  {guardando ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {tienePermiso("honda.actualizar_vehiculos") && (
        <HondaActualizacionDialog loteId={hondaLoteId} onClose={() => setHondaLoteId(null)} onAplicado={cargar} />
      )}
    </div>
  );
}
